import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Homepage product spotlight: size chips that switch the price and the variant, and an
 * Add to cart button that uses the theme's normal cart flow (so the cart count and drawer update).
 * The form underneath is a normal /cart/add form, so tapping the button before this script has
 * loaded still adds the item.
 */
class ProductSpotlight extends HTMLElement {
  /** @type {{id: number, price: string, compare: string, available: boolean}[]} */
  #variants = [];

  /** @type {number | undefined} */
  #resetTimer;

  connectedCallback() {
    const data = this.querySelector('[data-spotlight-variants]');
    try {
      this.#variants = data ? JSON.parse(data.textContent || '[]') : [];
    } catch (error) {
      console.error(error);
    }

    this.addEventListener('click', this.#onClick);
    this.querySelector('[data-spotlight-form]')?.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    clearTimeout(this.#resetTimer);
  }

  get #button() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-spotlight-add]'));
  }

  get #idInput() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const chip = event.target instanceof Element ? event.target.closest('[data-variant-id]') : null;
    if (!(chip instanceof HTMLElement) || !this.contains(chip)) return;
    this.#select(Number(chip.dataset.variantId));
  };

  /** @param {number} variantId */
  #select(variantId) {
    const variant = this.#variants.find((v) => v.id === variantId);
    if (!variant) return;

    this.querySelectorAll('[data-variant-id]').forEach((el) => {
      el.setAttribute('aria-checked', String(Number(/** @type {HTMLElement} */ (el).dataset.variantId) === variantId));
    });

    if (this.#idInput) this.#idInput.value = String(variantId);

    const price = this.querySelector('[data-spotlight-price]');
    if (price) price.textContent = variant.price;

    const compare = this.querySelector('[data-spotlight-compare]');
    if (compare instanceof HTMLElement) {
      compare.textContent = variant.compare;
      compare.hidden = !variant.compare;
    }

    clearTimeout(this.#resetTimer);
    this.#setIdle();
  }

  /** Puts the button into its normal state for the selected variant. */
  #setIdle() {
    const button = this.#button;
    if (!button) return;
    const variant = this.#variants.find((v) => v.id === Number(this.#idInput?.value));
    const available = !variant || variant.available;
    button.disabled = !available;
    button.textContent = available ? button.dataset.label || 'Add to cart' : button.dataset.soldOutLabel || 'Sold out';
  }

  /** @param {Event} event */
  #onSubmit = async (event) => {
    event.preventDefault();
    const button = this.#button;
    const variantId = this.#idInput?.value;
    if (!button || button.disabled || !variantId) return;

    button.disabled = true;
    button.textContent = button.dataset.addingLabel || 'Adding...';

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    });
    formData.append('sections', sectionIds.join(','));

    try {
      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: { ...fetchCfg.headers, Accept: 'text/html' },
      });
      const data = await response.json();

      if (data.status) {
        // e.g. the shopper already has every unit in stock in their cart
        button.textContent = data.message && data.message.length <= 22 ? data.message : 'Not available';
        this.#resetTimer = window.setTimeout(() => this.#setIdle(), 2500);
        return;
      }

      button.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'product-spotlight',
          itemCount: 1,
          sections: data.sections,
        })
      );

      button.textContent = button.dataset.addedLabel || 'Added';
      this.#resetTimer = window.setTimeout(() => this.#setIdle(), 2200);
    } catch (error) {
      console.error(error);
      this.#setIdle();
    }
  };
}

if (!customElements.get('product-spotlight')) {
  customElements.define('product-spotlight', ProductSpotlight);
}
